import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ExtensionDistribution {
  extension: string;
  count: number;
  percentage: number;
}

export interface BatchStatusCount {
  status: string;
  count: number;
  percentage: number;
}

export interface DashboardSummary {
  todayDocuments: number;
  todayBatches: number;
  todayIngestions: number;
  todayFailed: number;
  totalDocuments: number;
  totalBatches: number;
  batchesInProgress: number;
  activeBatches: number;
  subClassificationCount: number;
  evidenceSnippetCount: number;
  failedBatches: number;
  documentDistribution: ExtensionDistribution[];
  batchStatusOverview: BatchStatusCount[];
}

export interface DashboardSummaryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: DashboardSummary;
}

export interface TopClassification {
  name: string;
  count: number;
}

export interface Insights {
  classifiedDocumentCount: number;
  topClassifications: TopClassification[];
  distinctRoleCount: number;
  entityCount: number;
  relationshipCount: number;
  graphDocumentCount: number;
}

export interface InsightsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: Insights;
}

export interface DashboardBatchRow {
  id: string;
  name: string;
  classification: string | null;
  documents: number;
  evidence: number;
  accessRole: string | null;
  progress: number;
  status: string;
  updatedAt: string;
}

export interface DashboardBatchRowsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: DashboardBatchRow[];
}

export interface AccessRoleDocumentCount {
  roleName: string;
  documentCount: number;
}

export interface AccessRolesByDocCountResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: AccessRoleDocumentCount[];
}

export interface DocumentSource {
  provider: string;
  name: string;
  connectionCount: number;
  documentCount: number;
  status: string;
}

export interface DocumentSourcesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: DocumentSource[];
}

@Injectable({
  providedIn: 'root'
})
export class Dashboard {
  private baseUrl = `${environment.apiUrl}/dashboard`;

  constructor(private http: HttpClient) {}

  getSummary(): Observable<DashboardSummaryResponse> {
    return this.http.get<DashboardSummaryResponse>(`${this.baseUrl}/summary`);
  }

  getInsights(): Observable<InsightsResponse> {
    return this.http.get<InsightsResponse>(`${this.baseUrl}/insights`);
  }

  getBatchesInProgress(limit = 6, sourceType?: number): Observable<DashboardBatchRowsResponse> {
    const params: Record<string, number> = { limit };
    if (sourceType !== undefined) params['sourceType'] = sourceType;
    return this.http.get<DashboardBatchRowsResponse>(
      `${this.baseUrl}/batches-in-progress`,
      { params }
    );
  }

  getDocumentSources(): Observable<DocumentSourcesResponse> {
    return this.http.get<DocumentSourcesResponse>(`${this.baseUrl}/document-sources`);
  }

  getAccessRolesByDocCount(): Observable<AccessRolesByDocCountResponse> {
    return this.http.get<AccessRolesByDocCountResponse>(`${this.baseUrl}/access-roles`);
  }
}